import { Button } from "components/DesignSystem/Button/Button";
import { motion } from "framer-motion";
import React, { useContext } from "react";
// import { UpdateBulkTaskDeadline } from "./UpdateBulkTaskDeadline";
import { DividerLine } from "components/icons/DividerLine";
import { FILE_TYPE, FOLDER_TYPE } from "constants/documents";
import { useQuery } from "hooks/useQuery";
import { DocumentContext } from "./documentContext";

import ToolTip from "components/design/toolTip";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { DeleteIcon } from "components/icons/delete";
import { DownloadIcon } from "components/icons/Download";
import { Goto } from "components/icons/Goto";
import { Link } from "components/icons/Link";
import { MoveFile } from "components/icons/moveFile";
import { RenameIcon } from "components/icons/rename";
import { ICON_DEFAULT_COLOR } from "constants/documents";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { CaretDown } from "components/icons/CaretDown";

export type DocumentActionsProps = {
  onRename: () => void;
  onCopy: () => void;
  goToFolder: () => void;
  onDownload: (
    e?: React.MouseEvent<HTMLButtonElement>,
    exportToMail?: boolean
  ) => void;
  onDelete: () => void;
  onMove: () => void;
  onMoveToRecycleBin: () => void;
};

const Rename = ({ onRename }: { onRename: () => void }) => {
  return (
    <Button size="xsmall" onClick={onRename} title="Rename">
      Rename
    </Button>
  );
};

const CopyLink = ({ onCopy }: { onCopy: () => void }) => {
  return (
    <Button
      size="xsmall"
      onClick={onCopy}
      title="Copy link to share with teammates."
    >
      Copy link
    </Button>
  );
};

const GoToFolder = ({ goToFolder }: { goToFolder: () => void }) => {
  return (
    <Button size="xsmall" onClick={goToFolder} title="Go to folder">
      Go to folder
    </Button>
  );
};

const Download = ({
  onDownload,
}: {
  onDownload: (
    e?: React.MouseEvent<HTMLButtonElement>,
    exportToMail?: boolean
  ) => void;
}) => {
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild className="t-group">
        <div>
          <Button size="xsmall">
            <div className="t-flex t-items-center t-gap-1.5">
              Download
              <div className="group-data-state-open:t-rotate-180">
                <CaretDown />
              </div>
            </div>
          </Button>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content sideOffset={4} align="end" side="bottom">
          <ExportOptions
            onLocalDownload={() => onDownload(undefined, false)}
            onMailExport={() => onDownload(undefined, true)}
          />
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

const Delete = ({ onDelete }: { onDelete: () => void }) => {
  return (
    <Button
      customType="danger_outlined"
      size="xsmall"
      onClick={onDelete}
      title="Delete"
    >
      Delete
    </Button>
  );
};

const RecycleBin = ({ moveToRecycleBin }: { moveToRecycleBin: () => void }) => {
  return (
    <Button
      customType="danger_outlined"
      size="xsmall"
      onClick={moveToRecycleBin}
      title="Delete"
    >
      Move to recycle bin
    </Button>
  );
};

const Move = ({ onMove }: { onMove: () => void }) => {
  return (
    <Button size="xsmall" onClick={onMove} title="Move">
      Move
    </Button>
  );
};

export const DocumentBulkAction = ({
  goToFolder,
  onRename,
  onCopy,
  onDownload,
  onDelete,
  onMove,
  onMoveToRecycleBin,
}: DocumentActionsProps) => {
  const query = useQuery();
  const searchTerm = query.get("search");
  const { isAdmin } = useRoleBasedView();

  const { selectedFiles, entityFileAndFolder, selectedRecentDocs } =
    useContext(DocumentContext);

  const selectedFileData = [...selectedFiles, ...selectedRecentDocs];

  const isAllSelected = entityFileAndFolder.every(
    ({ isChecked }: { isChecked: boolean }) => isChecked
  );

  const isOnlyFolderSelected = selectedFileData.every(
    ({ type }) => type === FOLDER_TYPE
  );

  const isOnlyFilesSelected = selectedFileData.every(
    ({ type }) => type === FILE_TYPE
  );

  const selectedItemLength = selectedFileData.length;
  const isAnyFileLocked = selectedFileData.some(({ is_locked }) => is_locked);

  const singlefolderSelcted = isOnlyFolderSelected && selectedItemLength === 1;
  const singleFileSelected = isOnlyFilesSelected && selectedItemLength === 1;
  const muilpleFoldersSelected = isOnlyFolderSelected && selectedItemLength > 1;
  const muilpleFilesSelected = isOnlyFilesSelected && selectedItemLength > 1;
  const fileAndFolderSelected = !isOnlyFilesSelected && !isOnlyFolderSelected;
  const protectedFolderSelected = isAnyFileLocked;

  const renderButtons = () => {
    switch (true) {
      case protectedFolderSelected:
        return (
          <>
            <Download onDownload={onDownload} />
            {singlefolderSelcted && <CopyLink onCopy={onCopy} />}
            {isOnlyFilesSelected && <Move onMove={onMove} />}
          </>
        );
      case singlefolderSelcted:
        return (
          <>
            <Rename onRename={onRename} />
            <Download onDownload={onDownload} />
            <CopyLink onCopy={onCopy} />
            {isAdmin && <Delete onDelete={onDelete} />}
          </>
        );
      case singleFileSelected:
        return (
          <>
            <Rename onRename={onRename} />
            <Download onDownload={onDownload} />
            <CopyLink onCopy={onCopy} />
            <Move onMove={onMove} />
            {isAdmin ? (
              <RecycleBin moveToRecycleBin={onMoveToRecycleBin} />
            ) : (
              <Delete onDelete={onDelete} />
            )}
            {searchTerm && <GoToFolder goToFolder={goToFolder} />}
          </>
        );
      case muilpleFoldersSelected:
        return (
          <>
            <Download onDownload={onDownload} />
            {isAdmin && <Delete onDelete={onDelete} />}
          </>
        );
      case muilpleFilesSelected:
        return (
          <>
            <Download onDownload={onDownload} />
            <Move onMove={onMove} />
            {isAdmin ? (
              <RecycleBin moveToRecycleBin={onMoveToRecycleBin} />
            ) : (
              <Delete onDelete={onDelete} />
            )}
          </>
        );
      case fileAndFolderSelected:
        return (
          <>
            <Download onDownload={onDownload} />
          </>
        );

      case isAllSelected && isOnlyFilesSelected:
        return (
          <>
            <Download onDownload={onDownload} />
            {isAdmin ? (
              <RecycleBin moveToRecycleBin={onMoveToRecycleBin} />
            ) : (
              <Delete onDelete={onDelete} />
            )}
          </>
        );

      case isAllSelected:
        return (
          <>
            <Download onDownload={onDownload} />
            {isAdmin && <Delete onDelete={onDelete} />}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <motion.span
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ease: "easeInOut", duration: 0.2 }}
      //   key={selectedRows.toString()}
      className="t-flex t-gap-3 t-items-center"
    >
      {renderButtons()}
    </motion.span>
  );
};
